import type { ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import Button from "./Button";
import { cn } from "@/lib/utils";

export interface ErrorStateProps {
  title: string;
  message: string;
  onRetry?: () => void;
  /** Shown on the retry button when `onRetry` is set. */
  retryLabel?: string;
  isRetrying?: boolean;
  action?: ReactNode;
  className?: string;
}

export function ErrorState({
  title,
  message,
  onRetry,
  retryLabel = "Try again",
  isRetrying = false,
  action,
  className,
}: ErrorStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center px-6 py-16 text-center",
        className,
      )}
    >
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-error-600/20 bg-error-50">
        <AlertTriangle className="h-6 w-6 text-error-600" />
      </div>
      <h3 className="mb-1 text-base font-semibold tracking-tight text-surface-900">{title}</h3>
      <p className="mb-5 max-w-sm text-sm text-surface-500">{message}</p>
      <div className="flex items-center gap-3">
        {onRetry ? (
          <Button
            onClick={onRetry}
            variant="outline"
            isLoading={isRetrying}
            leftIcon={<RefreshCw className="h-4 w-4" />}
          >
            {retryLabel}
          </Button>
        ) : null}
        {action}
      </div>
    </div>
  );
}
